import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { ColorPicker } from '@atlassian/color-picker/ui';
import { isReadable } from '@atlassian/color-picker/utils/color-conversion';
import { useAutoTransitioningColors } from '@atlassian/color-picker/utils/use-autotransitioning-colors';
import { useBuiltInColorMap } from '@atlassian/color-picker/utils/use-built-in-color-map';
import { ImageField } from '@atlassian/company-hub-common/config-panel/image-field';
import { SegmentationTargetGroupsField } from '@atlassian/company-hub-common/config-panel/segmentation-target-groups-field';
import {
	UnsplashSearchField,
	type UnsplashSearchFieldProps,
} from '@atlassian/company-hub-common/config-panel/unsplash-search-field';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { ITEM_FIELDS as CARD_FIELDS } from '@atlassian/company-hub-common/types';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../../constants';
import { cardNewParametersV4 } from '../../../../../controllers/use-cards-extension-parameters-and-actions';
import type { UpdateCardFormFieldType } from '../../../../../controllers/use-cards-extension-parameters-and-actions/types';
import { CardField, type CardFieldProps } from './card-field';
import { messages } from './messages';

const styles = cssMap({
	wrapper: {
		alignSelf: 'stretch',
		flexDirection: 'column',
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	bgAndTextColorOptionsWrapper: {
		marginTop: token('space.200'),
	},
});

export type CardContentProps = {
	showBgAndTextColorOptions?: boolean;
	showLabel?: boolean;
	showMediaPicker?: boolean;
	showSegmentation: boolean;
	updateCardFormField: UpdateCardFormFieldType;
} & Pick<CardFieldProps, 'cardParameters' | 'isDisabled' | 'showDescription' | 'layout' | 'size'> &
	Pick<UnsplashSearchFieldProps, 'contentId' | 'initialUnsplashResults' | 'isNoImageAllowed'>;

// TODO: COMPHUB-3164 - replace this background color field with the reusable one once it's implemented. Refer to ticket for more context
export const CardContent = ({
	cardParameters,
	contentId,
	initialUnsplashResults,
	isDisabled,
	isNoImageAllowed,
	showDescription,
	layout,
	size,
	showSegmentation,
	showBgAndTextColorOptions,
	updateCardFormField,
	showMediaPicker,
	showLabel,
}: CardContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		cardId,
		imageSrc,
		imageAltText,
		confluencePageId,
		audienceSegmentationGroupIds,
		backgroundColor = cardNewParametersV4.backgroundColor,
		textColor = cardNewParametersV4.textColor,
	} = cardParameters;
	const { isSSRProductInDarkMode, isCompanyHub, enableLinkInputInMediaPicker, branding } =
		useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);

	const isReadableBackgroundColor = (color: string) => isReadable(color, textColor);
	const isReadableTextColor = (color: string) => isReadable(color, backgroundColor);

	const updateBackgroundColor = (bgColor: string) => {
		updateCardFormField(cardId, {
			[CARD_FIELDS.BACKGROUND_COLOR]: bgColor,
		});
	};

	const updateTextColor = (color: string) => {
		updateCardFormField(cardId, {
			[CARD_FIELDS.TEXT_COLOR]: color,
		});
	};

	useAutoTransitioningColors(
		backgroundColor,
		isReadableTextColor,
		textColor,
		updateTextColor,
		branding?.defaultParameters,
	);

	const handleOnGroupSelected = (selectedGroups: string[] | undefined) => {
		updateCardFormField(cardId, {
			[CARD_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: selectedGroups,
		});
	};

	return (
		<Flex xcss={styles.wrapper}>
			{showSegmentation && (
				<Box paddingBlock="space.100">
					<SegmentationTargetGroupsField
						targetGroupsIds={audienceSegmentationGroupIds}
						onGroupSelected={handleOnGroupSelected}
						showUpdatedStyles
						source={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
					/>
				</Box>
			)}
			<CardField
				cardParameters={cardParameters}
				updateCardFormField={updateCardFormField}
				isDisabled={isDisabled}
				showDescription={showDescription}
				showLabel={showLabel}
				layout={layout}
				size={size}
			/>
			{showMediaPicker ? (
				<ImageField
					shouldAllowLinkFromUrl={enableLinkInputInMediaPicker}
					analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
					itemId={cardId}
					contentId={contentId}
					imageSrc={imageSrc}
					imageAltText={imageAltText}
					updateItemFormField={updateCardFormField}
				/>
			) : (
				<UnsplashSearchField
					itemId={cardId}
					idPrefix="card"
					imageSrc={imageSrc}
					imageAltText={imageAltText}
					updateItemFormField={updateCardFormField}
					initialUnsplashResults={initialUnsplashResults}
					contentId={contentId}
					confluencePageId={confluencePageId}
					isNoImageAllowed={isNoImageAllowed}
					analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
				/>
			)}
			{showBgAndTextColorOptions && (
				<Stack space="space.200" xcss={styles.bgAndTextColorOptionsWrapper}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.backgroundColor} />
						</Text>
						<ColorPicker
							builtInColorMap={
								hideDefaultColorPalette && brandBackgroundColorMap
									? brandBackgroundColorMap
									: backgroundColorMap
							}
							isReadableColor={isReadableBackgroundColor}
							onChange={updateBackgroundColor}
							value={backgroundColor}
							pickerTitle={messages.backgroundColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
						/>
					</Inline>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.textColor} />
						</Text>
						<ColorPicker
							builtInColorMap={hideDefaultColorPalette ? emptyColorMap : textColorMap}
							isReadableColor={isReadableTextColor}
							onChange={updateTextColor}
							value={textColor}
							pickerTitle={messages.textColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
							showTextIcon
						/>
					</Inline>
				</Stack>
			)}
		</Flex>
	);
};
